<template>
  <div class="kpt-create-graph">
    <p class="title is-5 is-uppercase">Create Graph</p>
    <p class="subtitle is-6">Create a new graph description</p>

    <div class="create-graph-content columns">
      <div class="column is-5">
        <h5 class="has-text-weight-semibold">Select first metric:</h5>
        <div class="columns">
          <div class="column is-4">
            <b-field label="Type">
              <b-select placeholder="Select Provider..." 
                id="add-graph-select-type1" 
                v-model="firstProvider" 
                @change.native="resetFirstProvider" 
                expanded>
                <option v-for="(provider, index) in metricProviders" :key="index" :value="provider.shortcode">{{ provider.name }}</option>
              </b-select>
            </b-field>
          </div>
          <div class="column">
            <b-field label="Metric">
              <b-select placeholder="Choose..." id="add-graph-select-metric1" v-model="firstSelectedMetric" :disabled="!firstProvider" expanded>
                <option v-for="(metricType, index) in availableMetricsFirst" 
                  :value="metricType" 
                  :title="metricType.description" 
                  :key="index">{{metricType.name}}</option>
              </b-select>
            </b-field>
          </div>
        </div>

        <h5 class="has-text-weight-semibold">Select second metric:</h5>
        <div class="columns">
          <div class="column is-4">
            <b-field label="Type">
              <b-select placeholder="Select Provider..." 
                id="add-graph-select-type2" 
                v-model="secondProvider" 
                :disabled="disableSecondDropdown"
                @change.native="resetSecondProvider" 
                expanded>
                <option v-for="(provider, index) in metricProviders" :key="index" :value="provider.shortcode">{{ provider.name }}</option>
              </b-select>
            </b-field>
          </div>
          <div class="column">
            <b-field label="Metric">
              <b-select placeholder="Choose..." id="add-graph-select-metric2" v-model="secondSelectedMetric" :disabled="!secondProvider || disableSecondDropdown" expanded>
                <option v-for="(metricType, index) in availableMetricsSecond" 
                  :value="metricType" 
                  :title="metricType.description" 
                  :key="index">{{metricType.name}}</option>
              </b-select>
            </b-field>
          </div>
        </div>

        <b-field label="Name">
          <b-input v-model="newGraph.name" placeholder="(Optional)"></b-input>
        </b-field>
      </div>
    </div>

    <div class="buttons">
      <b-button type="is-primary is-outlined" size="is-small" icon-left="check" @click="addGraph">Add</b-button>
      <b-button type="is-primary is-outlined" size="is-small" icon-left="times" @click="$emit('cancel')">Cancel</b-button>
    </div>

  </div>
</template>

<script>
import { max, sortBy } from 'lodash';

export default {
  name: 'MetricChartCreate',
  methods: {
    // eslint-disable-next-line consistent-return
    async addGraph() {
      if (!this.metricProviders) return this.$buefy.toast.open({ type: 'is-danger', message: 'Metric providers not found.' });
      if (!this.firstProvider) return this.$buefy.toast.open({ type: 'is-danger', message: 'Please choose a Type for the first metric.' });

      this.newGraph = {
        name: this.newGraph.name ? this.newGraph.name : this.ifNoNameName,
        firstSelectedMetric: this.firstSelectedMetric,
        secondSelectedMetric: this.secondSelectedMetric,
      }

      const graphs = this.$store.state.user.settings.userGraphs;

      // fetch the top graph ID
      let topId = max(graphs.map(o => o.id));
      // no existing graphs found
      if (!topId) topId = 0;

      const shortCodes = [];
      if (this.newGraph.firstSelectedMetric) {
        const firstProvider = this.metricProviders.find(mp => mp.shortcode == this.firstProvider);
        if (!firstProvider) return this.$buefy.toast.open({ type: 'is-danger', message: 'Unable to identify first metric provider.' });
        shortCodes.push([
          firstProvider.shortcode,
          this.newGraph.firstSelectedMetric.shortcode,
          this.remote.id
        ]);
      }
      if (this.newGraph.secondSelectedMetric) {
        if (!this.secondProvider) return this.$buefy.toast.open({ type: 'is-danger', message: 'Please choose a Type for the second metric.' });
        const secondProvider = this.metricProviders.find(mp => mp.shortcode == this.secondProvider);
        if (!secondProvider) return this.$buefy.toast.open({ type: 'is-danger', message: 'Unable to identify second metric provider.' });
        shortCodes.push([
          secondProvider.shortcode,
          this.newGraph.secondSelectedMetric.shortcode,
          this.remote.id
        ]);
      }

      // add to the graphs array for user settings
      graphs.push({
        id: topId + 1,
        shortcodes: shortCodes,
        delimiter: null,
        timeSpan: { id: 'day', name: 'Last 24 hours' },
        startDate: null, // used for custom date range
        endDate: null, // used for custom date range
        name: this.newGraph.name,
        autoRefresh: false,
        dataColor1: '#65BD81',
        dataColor2: '#75A7D3',
        graphType: 'line'
      });

      this.$store.dispatch('updateSetting', { key: 'userGraphs', value: graphs });
      
      // emit success to inform parent component
      this.$emit('success', this.newGraph);
    },
    getSerialFromSelection(asmContext) {
      return this.remoteDevices[asmContext] || null;
    },
    resetFirstProvider() {
      this.firstSelectedMetric = null;
      this.resetSecondProvider();
    },
    resetSecondProvider() {
      this.secondSelectedMetric = null;
    }
  },
  computed: {
    remote() {
      return this.$store.state.remotes.remote;
    },
    metricProviders() {
      let providers = this.$store.state.remotes.metricProviders || [];
      if (providers.length) {
        providers = sortBy(providers, ['name']);
      }
      return providers;
    },
    metricTypes() {
      return this.$store.state.remotes.metricTypes;
    },
    ifNoNameName() {
      if (this.firstSelectedMetric && this.secondSelectedMetric) {
        return `${this.firstSelectedMetric.name} & ${this.secondSelectedMetric.name}`;
      } 
      if (this.firstSelectedMetric && !this.secondSelectedMetric) {
        return this.firstSelectedMetric.name;
      }
      return 'Metrics Graph';
    },
    disableSecondDropdown() {
      if (!this.firstSelectedMetric) return true;
      return !this.firstSelectedMetric.shortcode;
    },
    canSubmit() {
      return (this.firstSelectedMetric) && (this.firstSelectedMetric.shortcode);
    },
    availableMetricsFirst() {
      // check for empty properties
      if (!this.newGraph || !this.metricTypes || !this.firstProvider) return []; // something is missing - no options available

      // find metric types which pair to the selected provider
      const providerMetrics = this.metricTypes.find(mt => mt.providerShortcode == this.firstProvider);
      if (!providerMetrics || !providerMetrics.types || providerMetrics.types.length == 0) return [];
      
      // filter out hidden metrics that do not "plot" well for graphs
      const metricTypes = providerMetrics.types.filter(mt => !this.hiddenMetricTypes.includes(mt.shortcode));
      // return sorted list of metric types available for the selected provider
      return sortBy(metricTypes, ['name']);
    },
    availableMetricsSecond() {
      // check for empty properties
      if (!this.newGraph || !this.metricTypes || !this.secondProvider) return []; // something is missing - no options available

      // find metric types which pair to the selected provider
      const providerMetrics = this.metricTypes.find(mt => mt.providerShortcode == this.secondProvider);
      if (!providerMetrics || !providerMetrics.types || providerMetrics.types.length == 0) return [];

      // filter out hidden metrics that do not "plot" well for graphs
      let metricTypes = providerMetrics.types.filter(mt => !this.hiddenMetricTypes.includes(mt.shortcode));
      // prevent selection of the same metric when providers match
      if (this.firstSelectedMetric && this.firstProvider == this.secondProvider) metricTypes = metricTypes.filter(mt => this.firstSelectedMetric.shortcode != mt.shortcode);
      // return sorted list of metric types available for the selected provider
      return sortBy(metricTypes, ['name']);
    }
  },
  data() {
    return {
      newGraph: { 
        name: null,
        firstSelectedMetric: null, 
        secondSelectedMetric: null
      },
      isSaving: false,
      firstProvider: null,
      firstSelectedMetric: null, 
      secondProvider: null,
      secondSelectedMetric: null,
      hiddenMetricTypes: ['MT_BEAM'] // array of metric shortcodes which do not "plot" well on graphs
    }
  }
}
</script>

<style lang="scss" scoped>
.kpt-create-graph {
  h5 {
    margin-bottom: 15px;
  }
  &::v-deep .label {
    font-weight: 400;
  }
  .create-graph-content {
    margin-bottom: 15px;
  }
}
</style>
